import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  Button,
  Drawer,
  IconButton,
  TablePagination,
  TextField,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { toast } from "react-toastify";
import { deletePermission, getAllPermissions } from "../../api/services/api";
import {
  StyledDrawerContent,
  StyledDrawerHeader,
} from "../../components/DrawerTemplate";
import EmptyScreen from "../../components/EmptyScreen";
import { DeleteButton, EditButton } from "../../components/IconButtons";
import Loader from "../../components/Loader";
import Table from "../../components/TableComponent";
import { RoleGuard } from "../../components/RBAC";
import { PermissionForm } from "./PermissionForm";

export const Permissions = () => {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [limit, setLimit] = useState<number>(10);
  const [page, setPage] = useState<number>(0);
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState<any>(null);

  const { data, isLoading } = useQuery(
    [
      "permissions",
      {
        search: search,
        limit: limit,
        offset: limit * page,
      },
    ],
    getAllPermissions
  );

  const handlePageChange = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event: any) => {
    setLimit(parseInt(event.target.value, 10));
    setPage(0);
  };

  const { mutate: deletePermissionMutate } = useMutation(deletePermission, {
    onSuccess: (res: any) => {
      queryClient.invalidateQueries("permissions");
      toast.success(res?.data?.message || "Permission deleted successfully");
    },
    onError: (res: any) => {
      toast.error(
        res?.response?.data?.message || "Failed to delete permission"
      );
    },
  });

  const handleEditClick = (data: any) => {
    setFormData(data);
    toggleDrawer(true);
  };

  const handleAdd = () => {
    toggleDrawer(true);
    setFormData(null);
  };

  const handleDeleteClick = (id: any) => {
    if (window.confirm("Are you sure you want to delete this permission?")) {
      deletePermissionMutate(id);
    }
  };

  const toggleDrawer = (newOpen: any) => {
    setOpen(newOpen);
  };

  const Columns = [
    {
      title: "Permission Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
    },
    {
      title: "Resource",
      dataIndex: "resource",
      key: "resource",
    },
    {
      title: "Action",
      dataIndex: "action",
      key: "action",
    },
    {
      title: "Actions",
      dataIndex: "id",
      key: "id",
      render: (colData: any, row: any) => (
        <Box display="flex" gap="20px">
          <RoleGuard allowedRoles={["SUPER_ADMIN"]}>
            <EditButton onClick={() => handleEditClick(row)} />
            <DeleteButton onClick={() => handleDeleteClick(colData)} />
          </RoleGuard>
        </Box>
      ),
    },
  ];

  return (
    <Box my={2}>
      <Box mb={2} display="flex" gap={2} alignItems="center">
        <Typography variant="h3">Permissions</Typography>
        <RoleGuard allowedRoles={["SUPER_ADMIN"]}>
          <Button variant="contained" onClick={handleAdd}>
            + Add Permission
          </Button>
        </RoleGuard>
      </Box>
      <Box>
        <TextField
          label="Search permissions"
          sx={{ width: "450px", padding: "0px" }}
          InputProps={{
            endAdornment: (
              <IconButton>
                <SearchIcon />
              </IconButton>
            ),
          }}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </Box>
      <Box>
        {data?.data?.count === 0 ? (
          <EmptyScreen type="permissions" />
        ) : (
          <>
            {isLoading ? (
              <Loader />
            ) : (
              <Box mt={2}>
                <Table
                  key={"permissions"}
                  dataSource={data?.data?.data}
                  rowKey={"id"}
                  columns={Columns}
                />
                <Box
                  sx={{
                    marginTop: "20px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <TablePagination
                    count={data?.data?.count || 0}
                    page={page}
                    onPageChange={handlePageChange}
                    rowsPerPage={limit}
                    onRowsPerPageChange={handleRowsPerPageChange}
                  />
                </Box>
              </Box>
            )}
          </>
        )}
      </Box>
      <Drawer anchor="right" open={open} onClose={() => toggleDrawer(false)}>
        <StyledDrawerHeader>
          <Typography>
            {formData ? "Edit Permission" : "Create Permission"}
          </Typography>{" "}
          <Typography
            sx={{
              fontSize: "14px",
              fontFamily: "Inter_semibold",
              color: "black",
              opacity: "0.5",
              cursor: "pointer",
            }}
            onClick={() => toggleDrawer(false)}
          >
            Cancel
          </Typography>
        </StyledDrawerHeader>
        <StyledDrawerContent>
          <PermissionForm
            formData={formData}
            toggleDrawer={(e: any) => toggleDrawer(e)}
          />
        </StyledDrawerContent>
      </Drawer>
    </Box>
  );
};
